import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./searchableDropdown.module.css";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

interface SearchableDropdownProps<T> {
  fetchOptions: (search: string, cursor?: string) => Promise<{ items: T[]; pagination?: { next_cursor?: string } }>;
  onSelect: (item: T) => void;
  onClear?: () => void;
  getLabel: (item: T) => string;
  getValue: (item: T) => string;
  selectedValue?: string;
  selectedLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function SearchableDropdown<T>({
  fetchOptions,
  onSelect,
  onClear,
  getLabel,
  getValue,
  selectedValue,
  selectedLabel,
  placeholder = "Search…",
  disabled,
  className,
}: SearchableDropdownProps<T>) {
  const [query, setQuery] = useState(selectedLabel ?? "");
  const [options, setOptions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const fetchAndSet = useCallback(
    async (search: string) => {
      setIsLoading(true);
      try {
        const result = await fetchOptions(search);
        setOptions(result?.items ?? []);
      } catch {
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOptions],
  );

  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const maxHeight = Math.max(80, spaceBelow - 8);
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width, maxHeight });
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setIsOpen(true);
    if (!val) onClear?.();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAndSet(val), 300);
  };

  const handleFocus = () => {
    updateDropdownPosition();
    if (selectedValue && query) {
      setTimeout(() => inputRef.current?.select(), 0);
    }
    setIsOpen(true);
    if (options.length === 0 && !isLoading) {
      fetchAndSet(query);
    }
  };

  const handleClear = () => {
    setQuery("");
    setOptions([]);
    onClear?.();
    updateDropdownPosition();
    setIsOpen(true);
    fetchAndSet("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (item: T) => {
    setQuery(getLabel(item));
    setIsOpen(false);
    onSelect(item);
  };

  // Close when clicking outside both the input container and the portaled dropdown
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Close on any parent scroll (but not the dropdown's own list scroll).
  // Repositioning on every scroll event causes a feedback loop inside modals.
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleResize = () => updateDropdownPosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (selectedLabel !== undefined) setQuery(selectedLabel);
  }, [selectedLabel]);

  const dropdownList =
    isOpen && dropdownPos ? (
      <ul
        ref={dropdownRef}
        className={styles.dropdown}
        role="listbox"
        style={{
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          maxHeight: dropdownPos.maxHeight,
        }}
      >
        {isLoading ? (
          <li className={styles.stateItem}>Loading…</li>
        ) : options.length === 0 ? (
          <li className={styles.stateItem}>No results</li>
        ) : (
          <>
            {options.map((item) => (
              <li
                key={getValue(item)}
                role="option"
                aria-selected={getValue(item) === selectedValue}
                className={`${styles.option} ${
                  getValue(item) === selectedValue ? styles.optionSelected : ""
                }`}
                onMouseDown={() => handleSelect(item)}
              >
                {getLabel(item)}
              </li>
            ))}
          </>
        )}
      </ul>
    ) : null;

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className ?? ""}`}
    >
      <div className={styles.inputWrap}>
        <input
          ref={inputRef}
          className={`${styles.input} ${isOpen ? styles.inputOpen : ""}`}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
        />
        {query && !disabled && (
          <button
            className={styles.clearBtn}
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear selection"
          >
            {(FEATURES.curationSkin ? <OI n="close" size="1em" /> : "×")}
          </button>
        )}
      </div>
      {createPortal(dropdownList, document.body)}
    </div>
  );
}
